import { memo, useEffect, useState } from 'react';
import type { SidebarItem, SidebarGroup, SidebarItemId } from '../../types/typing';

interface LanguageSidebarProps {
  selected: SidebarItemId | null;
  onSelect: (item: SidebarItem) => void;
}

const GROUPS: SidebarGroup[] = [
  {
    title: 'English',
    items: [
      { id: 'english', label: 'English', backendLanguage: null },
    ],
  },
  {
    title: 'Languages',
    items: [
      { id: 'c',          label: 'C',          backendLanguage: 'c' },
      { id: 'cpp',        label: 'C++',        backendLanguage: 'cpp' },
      { id: 'java',       label: 'Java',       backendLanguage: null },
      { id: 'csharp',     label: 'C#',         backendLanguage: null },
      { id: 'python',     label: 'Python',     backendLanguage: 'python' },
      { id: 'javascript', label: 'JavaScript', backendLanguage: null },
      { id: 'typescript', label: 'TypeScript', backendLanguage: null },
      { id: 'sql',        label: 'SQL',        backendLanguage: null },
      { id: 'bash',       label: 'Bash',       backendLanguage: null },
      { id: 'yaml',       label: 'YAML',       backendLanguage: null },
    ],
  },
  {
    title: 'Red Hat',
    items: [
      { id: 'rhcsa', label: 'RHCSA',          backendLanguage: null },
      { id: 'rhce',  label: 'RHCE (Ansible)', backendLanguage: null },
    ],
  },
];

function getSavedCollapsed(): boolean {
  try { return localStorage.getItem('sidebar-collapsed') === 'true'; }
  catch { return false; }
}

const LanguageSidebar = memo(function LanguageSidebar({
  selected,
  onSelect,
}: LanguageSidebarProps) {
  const [collapsed, setCollapsed] = useState(getSavedCollapsed);

  useEffect(() => {
    try { localStorage.setItem('sidebar-collapsed', String(collapsed)); }
    catch { /* ignore */ }
  }, [collapsed]);

  return (
    <aside className={`lang-sidebar${collapsed ? ' lang-sidebar--collapsed' : ''}`}>
      <button
        className="sidebar-toggle"
        onClick={() => setCollapsed((c) => !c)}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        title={collapsed ? 'Expand' : 'Collapse'}
      >
        {collapsed ? '›' : '‹'}
      </button>

      <div className="sidebar-scroll">
        {GROUPS.map((group) => (
          <div key={group.title} className="sidebar-group">
            {!collapsed && (
              <p className="sidebar-group-title">{group.title}</p>
            )}
            <nav aria-label={group.title}>
              {group.items.map((item) => {
                const active = selected === item.id;
                const soon = item.backendLanguage === null;
                return (
                  <button
                    key={item.id}
                    className={[
                      'sidebar-lang-btn',
                      active ? 'sidebar-lang-btn--active' : '',
                      soon  ? 'sidebar-lang-btn--soon'   : '',
                    ].join(' ').trim()}
                    onClick={() => onSelect(item)}
                    title={collapsed ? item.label : undefined}
                    aria-pressed={active}
                  >
                    <span className="sidebar-lang-label">{item.label}</span>
                    {!collapsed && soon && (
                      <span className="sidebar-lang-soon">soon</span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        ))}
      </div>
    </aside>
  );
});

export default LanguageSidebar;
